import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STORAGE_KEY = 'trace-feedback-comments';
const MIGRATION_KEY = 'trace-feedback-localstorage-migrated';
const LEGACY_STORAGE_KEY = 'dg-feedback-comments';
const LEGACY_MIGRATION_KEY = 'vera-feedback-localstorage-migrated';
const SOURCE_APP = 'trace';

const FeedbackContext = createContext();

function hasAuthToken() {
  return Boolean(localStorage.getItem('token'));
}

function loadComments() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return ctx;
}

export function FeedbackProvider({ children }) {
  const [comments, setComments] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchComments = useCallback(async () => {
    if (!hasAuthToken()) {
      setComments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/feedback', { params: { source_app: SOURCE_APP } });
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('Failed to load feedback comments:', err);
      setError('Feedback kon niet geladen worden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    const migrateLegacyComments = async () => {
      if (!hasAuthToken()) return;
      if (localStorage.getItem(MIGRATION_KEY) === 'true' || localStorage.getItem(LEGACY_MIGRATION_KEY) === 'true') return;
      const legacyComments = loadComments();
      if (!legacyComments.length) {
        localStorage.setItem(MIGRATION_KEY, 'true');
        return;
      }
      try {
        await Promise.all(legacyComments.map((comment) => api.post('/feedback', {
          sourceApp: SOURCE_APP,
          text: comment.text,
          screenPath: comment.screenPath || '/',
          screenLabel: comment.screenLabel || 'Screen',
        })));
        localStorage.setItem(MIGRATION_KEY, 'true');
        await fetchComments();
      } catch (err) {
        console.warn('Failed to migrate local feedback comments:', err);
      }
    };

    migrateLegacyComments();
  }, [fetchComments]);

  const addComment = useCallback(async (text, screenPath, screenLabel) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return false;
    setError('');
    try {
      const res = await api.post('/feedback', {
        sourceApp: SOURCE_APP,
        text: trimmed,
        screenPath: screenPath || '/',
        screenLabel: screenLabel || 'Screen',
      });
      setComments((prev) => [res.data, ...prev]);
      return true;
    } catch (err) {
      console.warn('Failed to save feedback comment:', err);
      setError('Feedback kon niet opgeslagen worden.');
      return false;
    }
  }, []);

  const deleteComment = useCallback(async (id) => {
    setError('');
    try {
      await api.delete(`/feedback/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (err) {
      console.warn('Failed to delete feedback comment:', err);
      setError('Feedback kon niet verwijderd worden.');
      return false;
    }
  }, []);

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const togglePanel = useCallback(() => setPanelOpen((v) => !v), []);

  const value = {
    comments,
    loading,
    error,
    panelOpen,
    filter,
    setFilter,
    addComment,
    deleteComment,
    openPanel,
    closePanel,
    togglePanel,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}
